import React from 'react';
import { Alert, Input, Tag, Typography } from 'antd';
import DiffPanel from '../DiffPanel';
import { IntegrityChartPreview, PricingChartPreview, StatBandPreview } from '../PagePreview';
import { Help, Panel, S } from '../ui';

const { Text, Paragraph } = Typography;

interface Props {
  derived: any;
  source: any;
  publishedDerived: any;
  publishedVersion: number | null;
  validation: any;
  outstanding: string[];
  note: string;
  onNote: (value: string) => void;
  validating: boolean;
}

/**
 * Renders the commentary the way the website will.
 *
 * The editor shows a document; the page shows a panel of a particular width with
 * particular type. Reviewing the words in the editor tells you they are written, not how
 * they will read, so the review step renders the stored markup rather than repeating the
 * editing surface.
 */
const CommentaryPreview: React.FC<{ title: string; html: string; italic?: boolean }> = ({
  title, html, italic,
}) => (
  <Panel title={title}>
    {html?.trim() ? (
      <div
        style={{
          maxWidth: 620,
          fontSize: 14,
          lineHeight: 1.6,
          fontStyle: italic ? 'italic' : undefined,
        }}
        // Sanitised by the platform before it was stored. This is the CMS previewing what
        // it already holds, not rendering anything that just arrived from a user.
        dangerouslySetInnerHTML={{ __html: html }}
      />
    ) : (
      <Text type="secondary">Nothing written yet.</Text>
    )}
  </Panel>
);

/**
 * The last look before anything is saved or published.
 *
 * Everything on this step is read-only except the history note. The point is to see the
 * page as a page -- the dark band, both charts, the words -- rather than as the form that
 * produced it, and to see what is CHANGING against what is currently live.
 */
const StepReview: React.FC<Props> = ({
  derived, source, publishedDerived, publishedVersion, validation, outstanding, note, onNote,
  validating,
}) => (
  <div>
    <Help>
      How the page will read. Nothing here is editable except the note for the history,
      which is for your colleagues rather than for readers.
    </Help>

    {!!outstanding.length && (
      <Alert
        style={{ marginBottom: S.md }}
        type="info"
        showIcon
        message="Still to write before this can be published"
        description={
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {outstanding.map((o) => <li key={o}>{o}</li>)}
          </ul>
        }
      />
    )}

    {!!validation?.errors?.length && !outstanding.length && (
      <Alert
        style={{ marginBottom: S.md }}
        type="error"
        showIcon
        message="These stop it being published"
        description={
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {validation.errors.map((e: string) => <li key={e}>{e}</li>)}
          </ul>
        }
      />
    )}

    {!!validation?.warnings?.length && (
      <Alert
        style={{ marginBottom: S.md }}
        type="warning"
        showIcon
        message="Worth a look, but not blocking"
        description={
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {validation.warnings.map((w: string) => <li key={w}>{w}</li>)}
          </ul>
        }
      />
    )}

    <Panel
      title="The dark band at the top"
      aside={<Tag color="purple">calculated</Tag>}
    >
      <StatBandPreview derived={derived} />
    </Panel>

    <Panel title="Calyx Carbon Integrity Index">
      <IntegrityChartPreview derived={derived} />
    </Panel>

    <CommentaryPreview
      title="Summary beside the integrity chart"
      html={source?.commentary?.integrity ?? ''}
    />

    <Panel title="Calyx-ClearBlue Carbon Price-Integrity Index">
      <PricingChartPreview derived={derived} />
    </Panel>

    <CommentaryPreview
      title="The italic note under the price chart"
      html={source?.commentary?.note ?? ''}
      italic
    />

    <CommentaryPreview
      title="Summary beside the price chart"
      html={source?.commentary?.pricing ?? ''}
    />

    <Panel title={`What is changing${publishedVersion != null ? `, against live v${publishedVersion}` : ''}`}>
      {publishedDerived ? (
        <DiffPanel
          published={publishedDerived}
          draft={derived}
          publishedVersion={publishedVersion}
        />
      ) : (
        <Text type="secondary">Nothing is published yet, so everything here is new.</Text>
      )}
    </Panel>

    <Panel title="Note for the history">
      <Help>
        A line for whoever reads the history later: what changed and why. It never appears
        on the public page.
      </Help>
      <Input
        style={{ maxWidth: 420 }}
        placeholder="e.g. 19 Aug 2026 refresh"
        value={note}
        maxLength={500}
        onChange={(e) => onNote(e.target.value)}
      />
    </Panel>

    {validating && (
      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
        Rechecking the numbers.
      </Paragraph>
    )}
  </div>
);

export default StepReview;
